/**
 * Where the money in the iceberg goes. The first split is exact, by law: each contribution and
 * tax has one recipient (pension system, Employment Fund, Kela, municipality, state, parish,
 * Yle, insurers). The municipality's and the state's euros are then split by what those two spend
 * on, using published spending profiles (finnish-municipal-spending-2025.ts for the chosen
 * municipality, finnish-state-budget-2026.ts). Those second-level shares are proportional
 * attributions, not earmarks, and the page says so.
 */
import type { TaxIcebergResult } from './calculate-tax-iceberg';
import { municipalSpendingProfile, type MunicipalSpendingKey } from './finnish-municipal-spending-2025';
import { stateSpendingProfile, type StateSpendingKey } from './finnish-state-budget-2026';

export type DestinationKey = 'pensions' | 'employmentFund' | 'kela' | 'municipality' | 'state' | 'parish' | 'yle' | 'insurers';

export interface DestinationBreakdownItem {
  key: MunicipalSpendingKey | StateSpendingKey;
  sharePct: number;
  /** EUR/year attributed to this purpose. */
  amount: number;
}

export interface Destination {
  key: DestinationKey;
  /** EUR/year. */
  amount: number;
  /** The part withheld from the pay, EUR/year. */
  fromEmployee: number;
  /** The part the employer pays on top, EUR/year. */
  fromEmployer: number;
  /** Share of everything that leaves the iceberg (all destinations), %. */
  sharePct: number;
  breakdown?: DestinationBreakdownItem[];
}

export interface TaxDestinationsInput {
  result: TaxIcebergResult;
  /** A municipality in the rate table, or '' for the national spending profile. */
  municipality: string;
  includeVat: boolean;
}

export function taxDestinations({ result, municipality, includeVat }: TaxDestinationsInput): Destination[] {
  const emp = result.breakdown.employee;
  const er = result.breakdown.employer;
  const vat = includeVat ? result.salary.vat : 0;
  const raw: Array<Omit<Destination, 'sharePct'>> = [
    { key: 'pensions', fromEmployee: emp.contributions.pension, fromEmployer: er.tyel, amount: emp.contributions.pension + er.tyel },
    { key: 'employmentFund', fromEmployee: emp.contributions.unemployment, fromEmployer: er.unemploymentInsurance, amount: emp.contributions.unemployment + er.unemploymentInsurance },
    {
      key: 'kela',
      fromEmployee: emp.contributions.dailyAllowance + emp.taxes.medicalCare,
      fromEmployer: er.healthInsurance,
      amount: emp.contributions.dailyAllowance + emp.taxes.medicalCare + er.healthInsurance,
    },
    { key: 'municipality', fromEmployee: emp.taxes.municipal, fromEmployer: 0, amount: emp.taxes.municipal },
    { key: 'state', fromEmployee: emp.taxes.state + vat, fromEmployer: 0, amount: emp.taxes.state + vat },
    { key: 'parish', fromEmployee: emp.taxes.church, fromEmployer: 0, amount: emp.taxes.church },
    { key: 'yle', fromEmployee: emp.taxes.yle, fromEmployer: 0, amount: emp.taxes.yle },
    { key: 'insurers', fromEmployee: 0, fromEmployer: er.accidentInsurance + er.groupLife, amount: er.accidentInsurance + er.groupLife },
  ];
  const total = raw.reduce((s, d) => s + Math.max(0, d.amount), 0);
  const municipal = municipalSpendingProfile(municipality || undefined);
  const state = stateSpendingProfile();
  return raw
    .filter((d) => d.amount > 0.5)
    .map((d) => {
      const dest: Destination = { ...d, sharePct: total > 0 ? (d.amount / total) * 100 : 0 };
      // Profiles are rounded shares that sum to about 100; normalise so the parts add up to the
      // destination's amount exactly.
      const split = <K extends string>(profile: Record<K, number>): DestinationBreakdownItem[] => {
        const sum = (Object.values(profile) as number[]).reduce((a, b) => a + b, 0) || 100;
        return (Object.keys(profile) as K[]).map((key) => ({ key, sharePct: (profile[key] / sum) * 100, amount: (d.amount * profile[key]) / sum }));
      };
      if (d.key === 'municipality') dest.breakdown = split(municipal);
      if (d.key === 'state') dest.breakdown = split(state);
      if (dest.breakdown) dest.breakdown.sort((a, b) => b.sharePct - a.sharePct);
      return dest;
    })
    .sort((a, b) => b.amount - a.amount);
}
